import { api } from "@/services";
import { API_ENDPOINTS } from "@/config";
import { isAddress } from "viem";

export interface assetStats {
    name: string; // asset token symbol
    address: string; // asset token address
    decimals: number; // asset token decimals
    totalTVLAsset: string;
    totalTVLAssetNormalized: number;
    totalLiquidAsset: string;
    totalLiquidAssetNormalized: number;
    totalIlliquidAsset: string;
    totalIlliquidAssetNormalized: number;
    pendingRedemptionsAsset: string;
    pendingRedemptionsAssetNormalized: number;
}

export interface receiptStats {
    name: string; // receipt token symbol
    address: string; // receipt token address
    decimals: number; // receipt token decimals
    totalSupply: string; // bigint, amount in receipt token
    totalSupplyNormalized: number;
    totalLocked: string; // bigint, amount in receipt token
    totalLockedNormalized: number;
    totalStaked: string; // bigint, amount in receipt token
    totalStakedNormalized: number;
    totalUnwindingNormalized: number;
    receiptExchangeRate: string; // bigint, value with receipt token decimals
    receiptExchangeRateNormalized: number;
}

export interface stakedStats {
    name: string; // staked token symbol
    address: string; // staked token address
    decimals: number; // staked token decimals
    totalSupply: string; // bigint, amount in staked token
    totalSupplyNormalized: number;
    exchangeRate: string; // bigint, value with staked token decimals
    exchangeRateNormalized: number;
    lastWeekAPY: number; // computed from exchange rate 7d apart
    average7dAPY: number; // computed from exchange rate 7d apart
    average30dAPY: number; // computed from exchange rate 30d apart
}

export interface lockedStats {
    name: string;
    address: string;
    decimals: number;
    bucketMaturity: number; // in week, 1 = 1 week, 13 = 13 weeks
    totalSupply: string; // bigint, amount in locked token
    totalSupplyNormalized: number;
    totalLocked: string; // bigint, amount in locked token
    totalLockedNormalized: number;
    exchangeRate: string; // bigint, value with locked token decimals
    exchangeRateNormalized: number;
    lastWeekAPY: number; // computed from exchange rate 7d apart
    average7dAPY: number; // computed from exchange rate 7d apart
    average30dAPY: number; // computed from exchange rate 30d apart
}

export interface Farm {
    address: string;
    name: string;
    label: string;
    isPaused: boolean;
    underlyingAssetSymbol: string;
    underlyingAssetAddress: string;
    assets: string; // bigint, current farm value in asset token
    assetsNormalized: number;
    APY: number;
    apyTooltip: string;
    apyBase: number;
    apyRewards: number;
    exitApy: number; // LEGACY
    avgApy: number;
    avgApyTooltip: string;
    maturityTimestampMs: number;
    bucket: number;
    type: "PROTOCOL" | "LIQUID" | "ILLIQUID";
}

export interface ProtocolStats {
    asset: assetStats;
    receipt: receiptStats;
    staked: stakedStats;
    locked: Record<string, lockedStats>;
}

export interface ProtocolData {
    stats: ProtocolStats;
    farms: Farm[];
    params: ProtocolParam;
}

export interface ProtocolParam {
    illiquidTargetRatio: number;
    reserveRatio: number;
    liquidReturnMultiplier: number;
    illiquidReturnMultiplier: number;
    unwindingMultiplier: number;
    bucketMultipliers: number[];
    smootherRate: number; // USDC added per second to staked APY side
}

export interface ProtocolPNL {
    stakedProfit: string; // bigint, amount in receipt token
    stakedProfitNormalized: number;
    lockedProfit: { [bucket: string]: string }; // bigint, amount in receipt token
    lockedProfitNormalized: { [bucket: string]: number };
    unwindingProfit: string; // bigint, amount in receipt token
    unwindingProfitNormalized: number;
}

interface APIResponse {
    code: string;
    data: ProtocolData;
}

export const fetchProtocolData = async (): Promise<ProtocolData> => {
    try {
        const response = await api.get<APIResponse>(API_ENDPOINTS.PROTOCOL.DATA);
        const data = response.data.data;

        return data;
    } catch (error) {
        console.error("Error fetching protocol data:", error);
        throw error;
    }
};

export const fetchProtocolPNL = async (address: string): Promise<ProtocolPNL> => {
    if (!isAddress(address)) throw new Error("Address is required to fetch wallet APY");

    try {
        const response = await api.get(API_ENDPOINTS.PROTOCOL.PNL(address));
        return response.data.data as ProtocolPNL;
    } catch (error) {
        console.error("Error fetching wallet APY:", error);
        throw error;
    }
};
